import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Pill,
  Activity,
  HeartPulse,
  FlaskConical,
  ClipboardList,
  Mars,
  Venus,
  Leaf,
  Lock,
  ArrowRight,
  Sparkles,
  History,
  X,
  TrendingUp,
} from "lucide-react";
import { QuestionBox } from "../components/QuestionBox";
import { Disclaimer } from "../components/Disclaimer";
import { Seo, SITE } from "../lib/seo";
import { getCategories, getPopular, getLearnIndex } from "../lib/api";
import { getHistory, clearHistory } from "../lib/history";
import { useT, useLang } from "../lib/i18n";

const EXAMPLES = [
  "What does high blood pressure mean?",
  "What are the side effects of metformin?",
  "What causes erectile dysfunction?",
  "What does an elevated A1C mean?",
  "What is sciatica?",
  "What causes acid reflux?",
];

const EXAMPLES_ES = [
  "¿Qué significa la presión arterial alta?",
  "¿Cuáles son los efectos secundarios de la metformina?",
  "¿Qué causa la disfunción eréctil?",
  "¿Qué significa un A1C elevado?",
  "¿Qué es la ciática?",
  "¿Qué causa el reflujo ácido?",
];

const EXAMPLES_ZH = [
  "高血压是什么意思？",
  "二甲双胍有哪些副作用？",
  "什么会导致勃起功能障碍？",
  "A1C 偏高意味着什么？",
  "什么是坐骨神经痛？",
  "什么会导致胃酸反流？",
];

const EXAMPLES_VI = [
  "Huyết áp cao nghĩa là gì?",
  "Metformin có tác dụng phụ gì?",
  "Điều gì gây rối loạn cương dương?",
  "Chỉ số A1C cao nghĩa là gì?",
  "Đau thần kinh tọa là gì?",
  "Điều gì gây trào ngược axit?",
];

const EXAMPLES_BY_LANG = { en: EXAMPLES, es: EXAMPLES_ES, zh: EXAMPLES_ZH, vi: EXAMPLES_VI };

const CATEGORY_ICONS = {
  medication: Pill,
  symptom: Activity,
  disease: HeartPulse,
  lab_test: FlaskConical,
  procedure: ClipboardList,
  mens_health: Mars,
  womens_health: Venus,
  general_wellness: Leaf,
};

export default function Home() {
  const navigate = useNavigate();
  const t = useT();
  const { lang } = useLang();
  const examples = EXAMPLES_BY_LANG[lang] || EXAMPLES;
  const [categories, setCategories] = useState([]);
  const [recent, setRecent] = useState([]);
  const [popular, setPopular] = useState([]);
  const [wellness, setWellness] = useState([]);

  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
    getLearnIndex(lang)
      .then((pages) => setWellness(pages.filter((p) => p.section === "Wellness")))
      .catch(() => {});
    setRecent(getHistory());
  }, [lang]);

  useEffect(() => {
    getPopular(lang).then(setPopular).catch(() => {});
  }, [lang]);

  const ask = (q) => navigate(`/answer?q=${encodeURIComponent(q)}`);

  return (
    <div>
      <Seo
        title="AskMyDoctor AI — Simple answers to everyday medical questions"
        description="Ask any medical question and get a clear, plain-language answer backed by trusted U.S. government sources. Free health calculators, private, no sign-in."
        path="/"
        image={`${SITE}/og-default.jpg`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "AskMyDoctor AI",
            url: SITE,
            potentialAction: {
              "@type": "SearchAction",
              target: `${SITE}/answer?q={search_term_string}`,
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "AskMyDoctor AI",
            url: SITE,
            logo: `${SITE}/og-default.jpg`,
          },
        ]}
      />
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-teal-50 via-[#FAFAF7] to-[#FAFAF7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-10 text-center">
          <span className="inline-flex items-center gap-2 bg-white border border-teal-200 text-teal-700 font-semibold text-sm px-4 py-1.5 rounded-full shadow-sm mb-5">
            <Sparkles className="w-4 h-4" /> {t("hero_badge")}
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight text-slate-900">
            {t("hero_title_before")}{" "}
            <span className="text-teal-600">{t("hero_title_highlight")}</span>
          </h1>
          <p className="mt-4 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
            {t("hero_subtitle")}
          </p>

          <div className="mt-8 text-left">
            <QuestionBox onSubmit={ask} testIdPrefix="main" />
          </div>

          <div className="mt-4">
            <Disclaimer />
          </div>

          <div
            className="mt-3 flex items-center justify-center gap-2 text-base text-slate-500"
            data-testid="privacy-note"
          >
            <Lock className="w-4 h-4 shrink-0" />
            <span>
              {t("privacy_note")}
            </span>
          </div>
        </div>
      </section>

      {/* Example questions */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-4">
          {t("try_these")}
        </h2>
        <div className="flex flex-wrap gap-3">
          {examples.map((q, i) => (
            <button
              key={q}
              onClick={() => ask(q)}
              data-testid={`example-chip-${i}`}
              className="group inline-flex items-center gap-2 bg-white border-2 border-slate-200 hover:border-teal-400 hover:bg-teal-50 text-slate-700 text-base sm:text-lg px-5 py-3 rounded-full transition-all min-h-[48px]"
            >
              {q}
              <ArrowRight className="w-4 h-4 text-teal-500 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          ))}
        </div>
      </section>

      {/* Popular topics (most-asked across all visitors) */}
      {popular.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-2" data-testid="popular-topics">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> {t("popular_questions")}
          </h2>
          <div className="flex flex-wrap gap-3">
            {popular.map((p, i) => (
              <button
                key={p.question}
                onClick={() => ask(p.question)}
                data-testid={`popular-chip-${i}`}
                className="group inline-flex items-center gap-2 bg-teal-50 border-2 border-teal-200 hover:border-teal-400 hover:bg-teal-100 text-teal-900 text-base px-5 py-3 rounded-full transition-all min-h-[48px]"
              >
                {p.question}
                <ArrowRight className="w-4 h-4 text-teal-500 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Wellness quick-ask chips (surface new Wellness library topics) */}
      {wellness.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-2" data-testid="wellness-chips">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <Leaf className="w-4 h-4" /> {t("everyday_healthy")}
            </h2>
            <button
              onClick={() => navigate("/wellness")}
              data-testid="wellness-see-all"
              className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-600 hover:text-emerald-700"
            >
              {t("see_all")} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          {(() => {
            const now = new Date();
            const week = Math.floor((now - new Date(now.getFullYear(), 0, 0)) / 86400000 / 7);
            const featured = wellness[week % wellness.length];
            return (
              <button
                onClick={() => navigate(`/${featured.slug}`)}
                data-testid="wellness-featured"
                className="group w-full text-left mb-4 rounded-2xl border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-white hover:border-emerald-400 hover:shadow-md p-5 sm:p-6 transition-all"
              >
                <span className="inline-flex items-center gap-1.5 bg-emerald-600 text-white font-bold text-xs px-3 py-1 rounded-full">
                  <Leaf className="w-3.5 h-3.5" /> {t("topic_of_week")}
                </span>
                <h3 className="mt-3 font-heading font-extrabold text-2xl text-slate-900">{featured.title}</h3>
                {featured.blurb && <p className="mt-1.5 text-base text-slate-600">{featured.blurb}</p>}
                <span className="mt-3 inline-flex items-center gap-1.5 text-emerald-700 font-semibold text-base">
                  {t("explore_topic")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
            );
          })()}
          <div className="flex flex-wrap gap-3">
            {wellness.map((w, i) => (
              <button
                key={w.slug}
                onClick={() => navigate(`/${w.slug}`)}
                data-testid={`wellness-chip-${i}`}
                className="group inline-flex items-center gap-2 bg-white border-2 border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50 text-emerald-900 text-base px-5 py-3 rounded-full transition-all min-h-[48px]"
              >
                {w.question}
                <ArrowRight className="w-4 h-4 text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Recent questions (device-local, no sign-in) */}
      {recent.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-2" data-testid="recent-questions">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <History className="w-4 h-4" /> {t("your_recent")}
            </h2>
            <button
              onClick={() => {
                clearHistory();
                setRecent([]);
              }}
              data-testid="clear-history-button"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-rose-600"
            >
              <X className="w-4 h-4" /> {t("clear")}
            </button>
          </div>
          <div className="flex flex-wrap gap-3">
            {recent.map((h, i) => (
              <button
                key={h.ts}
                onClick={() => ask(h.question)}
                data-testid={`recent-chip-${i}`}
                className="group inline-flex items-center gap-2 bg-white border-2 border-slate-200 hover:border-teal-400 hover:bg-teal-50 text-slate-700 text-base px-4 py-2.5 rounded-full transition-all min-h-[44px]"
              >
                {h.question}
                <ArrowRight className="w-4 h-4 text-teal-500 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Categories */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-6">
          {t("browse_topic")}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-5">
          {categories.map((c) => {
            const Icon = CATEGORY_ICONS[c.key] || HeartPulse;
            const label = t(`cat_${c.key}`);
            const example = t(`catex_${c.key}`);
            return (
              <button
                key={c.key}
                onClick={() => ask(example)}
                data-testid={`category-card-${c.key}`}
                className="group text-left bg-white border-2 border-slate-200 hover:border-teal-400 rounded-2xl p-5 transition-all hover:shadow-lg min-h-[132px] flex flex-col"
              >
                <span className="grid place-items-center w-12 h-12 rounded-xl bg-teal-100 text-teal-700 group-hover:bg-teal-600 group-hover:text-white transition-colors mb-3">
                  <Icon className="w-6 h-6" />
                </span>
                <span className="font-heading font-bold text-lg text-slate-900">
                  {label}
                </span>
                <span className="mt-1 text-sm text-slate-500 leading-snug line-clamp-2">
                  {example}
                </span>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
